import { ReactNode, useContext, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useEmbedTagsGetApi, useSiteGetApi } from '@/common/api/modules/site'
import { useSystemConfigGetApi } from '@/common/api/modules/systemConfig'
import { EmbedMailTag } from '@/common/api/params/site'
import {
  BUNDLE_CSS_CLASSES,
  BUNDLE_CSS_CLASSES_ANCHOR
} from '@/common/app/modules/recommend/widget'
import { distributionListPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { copyTextToClipboard } from '@/common/util/clipboard'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Code from '@/components/view/atoms/code/Code'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import SimpleTable from '@/components/view/atoms/table/SimpleTable'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import MessageBar, { MessageType } from '@/components/view/molecules/bar/MessageBar'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import HtmlPreviewFrame from '@/components/view/molecules/preview/HtmlPreviewFrame'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import AppTitleBadge from '@/components/container/layout/AppTitleBadge'
import W2ModeMessage from '@/components/container/modules/master/master/W2ModeMessage'
import { ToastMessageContext } from '@/components/container/utils/message/ToastMessageProvider'

/* ============================
   TagSection
   ============================ */
interface TagSectionProps {
  title: string | ReactNode
  desc: React.ReactNode
  code: string
  children?: React.ReactNode
  copyToClipboard: (text: string) => void
}

const TagSection = ({ title, desc, code, copyToClipboard, children }: TagSectionProps) => {
  return (
    <Area>
      <Heading subText={desc}>
        <ItemAlign gapX={4} align='center'>
          <Icon type={IconType.CODE_SQUARE} color={TextColor.SECONDARY} size={20} />
          {title}
        </ItemAlign>
      </Heading>
      <div className='border-secondary border-t px-6 pb-6'>
        <Code onClick={() => copyToClipboard(code)}>{code}</Code>
        {children}
      </div>
    </Area>
  )
}

/* ============================
   ParamTable
   ============================ */
interface ParamTableProps {
  rows: [string, string, string][]
}

const ParamTable = ({ rows }: ParamTableProps) => (
  <SimpleTable>
    <thead>
      <TableRow head noBorder>
        <TableHeadItem type='thin'>
          <Text size={12} weight='bold' color={TextColor.SECONDARY}>
            パラメータ
          </Text>
        </TableHeadItem>
        <TableHeadItem type='thin'>
          <Text size={12} weight='bold' color={TextColor.SECONDARY}>
            説明
          </Text>
        </TableHeadItem>
        <TableHeadItem type='thin'>
          <Text size={12} weight='bold' color={TextColor.SECONDARY}>
            例
          </Text>
        </TableHeadItem>
      </TableRow>
    </thead>
    <tbody>
      {rows.map(([param, desc, example], i) => (
        <TableRow key={i}>
          <TableRowItem width={160}>
            <Text size={12}>{param}</Text>
          </TableRowItem>
          <TableRowItem>
            <Text size={12}>{desc}</Text>
          </TableRowItem>
          <TableRowItem width={128}>
            <Text size={12}>{example}</Text>
          </TableRowItem>
        </TableRow>
      ))}
    </tbody>
  </SimpleTable>
)

/* ============================
   BundleCssClassSection
   ============================ */

// バンドルのカスタムCSSの記述例（導入先の企業名は入れない）
const BUNDLE_CSS_EXAMPLE = `/* 例：ボタンの色と角丸を変える */
.w2sr-bundle-cart {
  background: #135f63;
  border-radius: 999px;
}
/* 例：合計の下の注記を出さない */
.w2sr-bundle-note {
  display: none;
}`

const BundleCssClassSection = () => {
  // パーツ編集画面から #bundle-css-classes 付きで開かれたとき、一覧の位置まで移動する
  useEffect(() => {
    if (window.location.hash === `#${BUNDLE_CSS_CLASSES_ANCHOR}`) {
      document.getElementById(BUNDLE_CSS_CLASSES_ANCHOR)?.scrollIntoView({ block: 'start' })
    }
  }, [])

  return (
    <Area id={BUNDLE_CSS_CLASSES_ANCHOR}>
      <Heading subText='レイアウトが「バンドル（まとめてカートに入れる）」のレコメンドパーツで、カスタムCSSに使えるクラス名です。設置するコードは「レコメンドコード（商品詳細ページ）」と同じです。'>
        <ItemAlign gapX={4} align='center'>
          <Icon type={IconType.CODE_SQUARE} color={TextColor.SECONDARY} size={20} />
          バンドルのカスタムCSSクラス名
        </ItemAlign>
      </Heading>
      <div className='border-secondary border-t px-6 pb-6'>
        <Code>{BUNDLE_CSS_EXAMPLE}</Code>
        <SimpleTable>
          <thead>
            <TableRow head noBorder>
              <TableHeadItem type='thin'>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  クラス名
                </Text>
              </TableHeadItem>
              <TableHeadItem type='thin'>
                <Text size={12} weight='bold' color={TextColor.SECONDARY}>
                  付いている場所
                </Text>
              </TableHeadItem>
            </TableRow>
          </thead>
          <tbody>
            {BUNDLE_CSS_CLASSES.map(({ className, description }) => (
              <TableRow key={className}>
                <TableRowItem width={240}>
                  <Text size={12}>.{className}</Text>
                </TableRowItem>
                <TableRowItem>
                  <Text size={12}>{description}</Text>
                </TableRowItem>
              </TableRow>
            ))}
          </tbody>
        </SimpleTable>
      </div>
    </Area>
  )
}

/* ============================
   MailTab
   ============================ */
// 2026-09-13 作り直し: 以前は開封計測にもクリック計測と同じ {tracking_url} を表示しており、
// 配信ごとのタグも出していなかった（openspec/changes/mail-recommend）

const MAIL_ORIGIN_LABEL: Record<string, string> = {
  recipient: '受信者に合わせて表示',
  item: '購入商品の併売を表示'
}

interface MailTagCardProps {
  mailTag: EmbedMailTag
  copyToClipboard: (text: string) => void
}

// 配信1件分のメール用コード。コードは量が多いため既定ではたたみ、コピーと見本を中心に見せる
const MailTagCard = ({ mailTag, copyToClipboard }: MailTagCardProps) => {
  const [showCode, setShowCode] = useState(false)
  const rows = Math.ceil(mailTag.slots / Math.max(1, mailTag.columns))
  const markers = mailTag.placeholders.map((p) => p.placeholder).filter((m) => m)

  return (
    <Area>
      <Heading
        subText={`埋め込みコード：${mailTag.code} / ロジック：${mailTag.display_logic_label || mailTag.display_logic} / ${mailTag.slots}枠（${mailTag.columns}列×${rows}行）`}
      >
        <ItemAlign gapX={4} align='center'>
          <Icon type={IconType.CODE_SQUARE} color={TextColor.SECONDARY} size={20} />
          {mailTag.name}
          <Badge badgeType={BadgeType.SQUARE_SECONDARY}>
            {MAIL_ORIGIN_LABEL[mailTag.origin] ?? mailTag.origin}
          </Badge>
        </ItemAlign>
      </Heading>
      <div className='border-secondary flex flex-col gap-4 border-t px-6 pt-4 pb-6'>
        <ItemAlign gapX={4} align='center'>
          <Button color={ButtonColor.PRIMARY} onClick={() => copyToClipboard(mailTag.tag)}>
            コードをコピー
          </Button>
          <Button color={ButtonColor.SECONDARY} onClick={() => setShowCode((v) => !v)}>
            {showCode ? 'コードを隠す' : 'コードを表示'}
          </Button>
        </ItemAlign>
        {showCode && <Code onClick={() => copyToClipboard(mailTag.tag)}>{mailTag.tag}</Code>}
        <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
          コード内の {markers.join('・')}{' '}
          を、メール配信の差し込み変数に置き換えてから本文に貼ってください。同じ目印はすべて同じ変数なので、一括置換できます。
        </Text>
        <ParamTable
          rows={mailTag.placeholders.map((p) => [
            p.placeholder || `${p.key}=`,
            `${p.label}：${p.description}`,
            `${p.key}=`
          ])}
        />
        <div className='flex flex-col gap-2'>
          <Text size={12} weight='bold' color={TextColor.SECONDARY}>
            見本（差し込みが無いため人気商品で表示しています。見本はクリックできません）
          </Text>
          <HtmlPreviewFrame
            html={mailTag.preview_html}
            title={`${mailTag.name} の見本`}
            width={600}
          />
        </div>
      </div>
    </Area>
  )
}

interface MailTabProps {
  siteId: number
  copyToClipboard: (text: string) => void
}

const MailTab = ({ siteId, copyToClipboard }: MailTabProps) => {
  const { data, isLoading, error } = useEmbedTagsGetApi({ site_id: siteId })
  const mailTags = data?.w2sr_mail_tags ?? []

  return (
    <>
      <div className='-mb-4'>
        <Text size={16} weight='bold' tag='h2'>
          メール用レコメンド
        </Text>
        <Text size={12} color={TextColor.SECONDARY}>
          メール配信の本文（HTML）に貼るコードです。メールを開いた時点で、受信者ごとのおすすめ商品がレコメンドパーツと同じデザインの画像で表示され、クリックすると商品ページへ移動します。
        </Text>
      </div>
      {isLoading ? (
        <PageListLoading small />
      ) : data && !data.mail_reco_enabled ? (
        // 2026-10-06 追加: サイトは ON でも、オペレーターに mail_reco の権限が無いときはコードを出さない
        <Area>
          <div className='p-6'>
            <Text size={14} color={TextColor.SECONDARY}>
              メール用レコメンドのコードを表示する権限がありません。サイトの管理者に権限テンプレートの設定を確認してください。
            </Text>
          </div>
        </Area>
      ) : error ? (
        <Area>
          <div className='p-6'>
            <Text size={14} color={TextColor.CRITICAL}>
              メール用のコードを読み込めませんでした。時間をおいて画面を開き直してください。
            </Text>
          </div>
        </Area>
      ) : mailTags.length === 0 ? (
        <Area>
          <div className='flex flex-col gap-2 p-6'>
            <Text size={14} weight='bold'>
              メールで使える配信がありません
            </Text>
            <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
              配信中またはABテスト中で、次の条件をすべて満たす配信にメール用のコードが作られます。
            </Text>
            <Text size={12} lineHeight={150}>
              ・レコメンドパーツが商品レコメンドで、レイアウトがバンドル以外
              <br />
              ・全バリアントのロジックが「あなたへのおすすめ」系（mail / personal / adaptive /
              ranking_blend / recently_viewed）か、「購入商品の併売」系（BB / OB /
              similar）のどちらか一方にそろっている
            </Text>
            <Link to={distributionListPath(siteId)} className='text-brand text-[12px] underline'>
              配信の一覧を開く
            </Link>
          </div>
        </Area>
      ) : (
        mailTags.map((mailTag) => (
          <MailTagCard key={mailTag.code} mailTag={mailTag} copyToClipboard={copyToClipboard} />
        ))
      )}
    </>
  )
}

interface TabProps {
  distributionCode: string
  isW2: boolean
  baseUrl: string
  enableFullBehavior: boolean
  copyToClipboard: (text: string) => void
}

const TrackingTab = ({
  distributionCode,
  baseUrl,
  isW2,
  enableFullBehavior,
  copyToClipboard
}: TabProps) => {
  return (
    <>
      {/* W2連動モード */}
      {isW2 && <W2ModeMessage message='各コードにバリエーションID・ブランドIDが含まれます。' />}
      {/* --- データ取得用コード --- */}
      <div className='-mb-4'>
        <Text size={16} weight='bold' tag='h2'>
          トラッキングコード
        </Text>
        <Text size={12} color={TextColor.SECONDARY}>
          ユーザーの行動データ（閲覧・カート・購入）を取得するためのコードです。
        </Text>
        <Text size={12} color={TextColor.SECONDARY}>
          このコードは AIレコメンドと AI購入支援（Findee）で共通です。設置は1本で足ります。 Findee
          の経由CVR・GMV も、注文完了ページの購入コードから計測します。
        </Text>
      </div>
      <TagSection
        title='基本コード（全ページ共通）'
        desc={
          <>
            ECサイトの全ページの <code className='text-[11px]'>&lt;head&gt;</code>{' '}
            に設置してください。JS読込とサイトID設定を行います。
          </>
        }
        code={`<!-- W2SmartRec 基本コード - 全ページの <head> に設置 -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['setMerchantId', '${distributionCode}']);\n  _w2SmartRec.push(['setCustomerId', '{顧客ID}']);\n  (function(){var d=document,s=d.createElement('script');\n  s.src='${baseUrl}/js/w2smartrec.js';s.async=true;\n  s.onerror=function(){};d.head.appendChild(s);})();\n</script>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['setMerchantId', 'マーチャントID（サイトを識別する固有ID）', distributionCode],
            [
              'setCustomerId',
              '顧客ID（ECサイトの会員ID。ログイン中は会員IDを出力、未ログイン時は空文字でOK）',
              'N000737806'
            ]
          ]}
        />
        {/* <div className='mt-4 rounded-xl border border-l-4 border-[#c7d2fe] border-l-[#6366f1] bg-white p-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge badgeType={BadgeType.SQUARE_PRIMARY}>有料オプション</Badge>
            {enableFullBehavior && <Badge badgeType={BadgeType.SUCCESS}>利用中</Badge>}
            <Text size={14} weight='bold'>
              顧客カルテオプション：行動履歴を全取得
            </Text>
          </div>
          <div className='mt-2'>
            <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
              サイト管理でONにすると、基本コードが設置されている全ページで、閲覧コード（商品詳細・カテゴリ用）を個別に設置しなくても、ページ訪問を自動記録します。基本コードに{' '}
              <code className='rounded bg-[rgba(0,0,0,0.06)] px-1 text-[11px]'>setOption</code>{' '}
              が自動追加されます。
            </Text>
          </div>
          <div className='mt-2'>
            <Text
              size={12}
              color={enableFullBehavior ? TextColor.SUCCESS : TextColor.CRITICAL}
              weight='bold'
            >
              {enableFullBehavior ? '※ 現在ご利用中です。' : '※ ご利用には、お申し込みが必要です。'}
            </Text>
          </div>
        </div> */}
      </TagSection>
      <TagSection
        title={
          isW2 ? (
            <>
              商品カテゴリページ 閲覧コード
              <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY}>W2完全連動</Badge>
            </>
          ) : (
            '商品カテゴリページ 閲覧コード'
          )
        }
        desc={
          isW2
            ? '商品カテゴリ一覧ページに設置してください。商品カテゴリIDとブランドIDを送信します。'
            : '商品カテゴリ一覧ページに設置してください。商品カテゴリIDを送信します。'
        }
        code={
          isW2
            ? `<!-- W2SmartRec 閲覧コード（商品カテゴリページ）[W2連動] -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    category_id: '{商品カテゴリID}',\n    brand_id: '{ブランドID}'\n  }]);\n</script>`
            : `<!-- W2SmartRec 閲覧コード（商品カテゴリページ） -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    category_id: '{商品カテゴリID}'\n  }]);\n</script>`
        }
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={
            isW2
              ? [
                  ['category_id', '商品カテゴリID', 'CAT01'],
                  ['brand_id', 'ブランドID', 'BRD001']
                ]
              : [['category_id', '商品カテゴリID', 'CAT01']]
          }
        />
      </TagSection>
      <TagSection
        title={
          isW2 ? (
            <>
              閲覧コード（商品詳細ページ）
              <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY}>W2完全連動</Badge>
            </>
          ) : (
            '閲覧コード（商品詳細ページ）'
          )
        }
        desc={
          isW2
            ? '商品詳細ページに設置してください。商品ID・バリエーションID・商品カテゴリID・ブランドIDを送信します。'
            : '商品詳細ページに設置してください。商品IDと商品カテゴリIDを送信します。'
        }
        code={
          isW2
            ? `<!-- W2SmartRec 閲覧コード（商品詳細ページ）[W2連動] -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    item_id: '{商品ID}',\n    item_variation_id: '{バリエーションID}',\n    category_id: '{商品カテゴリID}',\n    brand_id: '{ブランドID}'\n  }]);\n</script>`
            : `<!-- W2SmartRec 閲覧コード（商品詳細ページ） -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    item_id: '{商品ID}',\n    category_id: '{商品カテゴリID}'\n  }]);\n</script>`
        }
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={
            isW2
              ? [
                  ['item_id', '商品ID（ECサイト側の商品コード）', 'SKU001'],
                  ['item_variation_id', '商品バリエーションID（サイズ・カラー等）', 'S-WHITE'],
                  ['category_id', '商品カテゴリID', 'CAT01'],
                  ['brand_id', 'ブランドID', 'BRD001']
                ]
              : [
                  ['item_id', '商品ID（ECサイト側の商品コード）', 'SKU001'],
                  ['category_id', '商品カテゴリID（任意）', 'CAT01']
                ]
          }
        />
      </TagSection>
      <TagSection
        title='閲覧コード（特集ページ）'
        desc='特集ページ・記事ページに設置してください。特集ページIDとページURLを送信し、特集リライト判定のアクセス数・滞在時間の計測に使用します。'
        code={`<!-- W2SmartRec 閲覧コード（特集ページ） -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    feature_page_id: '{特集ページID}',\n    page_url: location.href\n  }]);\n</script>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['feature_page_id', '特集ページID（W2のfeature_page_id）', 'FP001'],
            [
              'page_url',
              'ページURL（自動取得: location.href）',
              'https://example.com/feature/ai-guide'
            ]
          ]}
        />
      </TagSection>
      <TagSection
        title='閲覧コード（特集カテゴリページ ）'
        desc='特集カテゴリ一覧ページに設置してください。特集カテゴリIDを送信します。'
        code={`<!-- W2SmartRec 閲覧コード（特集カテゴリページ） -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['view', {\n    feature_category_id: '{特集カテゴリID}'\n  }]);\n</script>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable rows={[['feature_category_id', '特集カテゴリID', 'FC01']]} />
      </TagSection>
      <TagSection
        title={
          isW2 ? (
            <>
              カートコード（カートページ）
              <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY}>W2完全連動</Badge>
            </>
          ) : (
            'カートコード（カートページ）'
          )
        }
        desc={
          isW2
            ? 'カートページに設置してください。カート内の商品ID・バリエーションID一覧を送信します。'
            : 'カートページに設置してください。カート内の商品ID一覧を送信します。'
        }
        code={
          isW2
            ? `<!-- W2SmartRec カートコード [W2連動] -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['cart', {\n    items: [\n      { item_id: '{商品ID1}', item_variation_id: '{バリエーションID1}', price: {単価1}, quantity: {数量1} },\n      { item_id: '{商品ID2}', item_variation_id: '{バリエーションID2}', price: {単価2}, quantity: {数量2} }\n    ],\n    customer_id: '{顧客ID}'\n  }]);\n</script>`
            : `<!-- W2SmartRec カートコード -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['cart', {\n    items: [\n      { item_id: '{商品ID1}', price: {単価1}, quantity: {数量1} },\n      { item_id: '{商品ID2}', price: {単価2}, quantity: {数量2} }\n    ],\n    customer_id: '{顧客ID}'\n  }]);\n</script>`
        }
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={
            isW2
              ? [
                  ['items[].item_id', '商品ID', 'SKU001'],
                  ['items[].item_variation_id', 'バリエーションID', 'S-WHITE'],
                  ['items[].price', '単価', '1000'],
                  ['items[].quantity', '数量', '2'],
                  ['customer_id', '顧客ID（ログイン中の場合）', 'N000737806']
                ]
              : [
                  ['items[].item_id', '商品ID', 'SKU001'],
                  ['items[].price', '単価', '1000'],
                  ['items[].quantity', '数量', '2'],
                  ['customer_id', '顧客ID（ログイン中の場合）', 'C001']
                ]
          }
        />
      </TagSection>
      <TagSection
        title={
          isW2 ? (
            <>
              購入完了コード（注文完了ページ）
              <Badge badgeType={BadgeType.SQUARE_BRAND_SECONDARY}>W2完全連動</Badge>
            </>
          ) : (
            '購入完了コード（注文完了ページ）'
          )
        }
        desc={
          isW2
            ? '購入完了ページに設置してください。受注データをサーバーに送信し、W2商品在庫を自動減算します。'
            : '購入完了ページに設置してください。受注データをサーバーに送信します。'
        }
        code={
          isW2
            ? `<!-- W2SmartRec 購入完了コード [W2連動] -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['purchase', {\n    items: [\n      { item_id: '{商品ID1}', item_variation_id: '{バリエーションID1}', price: {単価1}, quantity: {数量1} },\n      { item_id: '{商品ID2}', item_variation_id: '{バリエーションID2}', price: {単価2}, quantity: {数量2} }\n    ],\n    order_id: '{受注ID}',\n    total_price: {合計金額},\n    customer_id: '{顧客ID}'\n  }]);\n</script>`
            : `<!-- W2SmartRec 購入完了コード -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['purchase', {\n    items: [\n      { item_id: '{商品ID1}', price: {単価1}, quantity: {数量1} },\n      { item_id: '{商品ID2}', price: {単価2}, quantity: {数量2} }\n    ],\n    order_id: '{受注番号}',\n    total_price: {合計金額},\n    customer_id: '{顧客ID}'\n  }]);\n</script>`
        }
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={
            isW2
              ? [
                  ['items[].item_id', '商品ID', 'SKU001'],
                  ['items[].item_variation_id', 'バリエーションID', 'S-WHITE'],
                  ['items[].price', '単価', '1000'],
                  ['items[].quantity', '数量', '2'],
                  // 2026-09-13 修正: メール用レコメンドは受注ID・会員IDで購入コードの受注と突き合わせるため、
                  // W2 の値をそのまま渡す案内にする（暗号化した値や受注番号を渡すと一致しない）
                  ['order_id', '受注ID（W2の受注IDをそのまま）', '20260913-000123'],
                  ['total_price', '合計金額', '3000'],
                  ['customer_id', '会員ID（W2の会員IDをそのまま。暗号化しない）', 'U0000123']
                ]
              : [
                  ['items[].item_id', '商品ID', 'SKU001'],
                  ['items[].price', '単価', '1000'],
                  ['items[].quantity', '数量', '2'],
                  ['order_id', '受注番号', 'ORD-001'],
                  ['total_price', '合計金額', '3000'],
                  ['customer_id', '顧客ID（暗号化推奨）', 'C001']
                ]
          }
        />
      </TagSection>
    </>
  )
}

const DisplayTab = ({
  distributionCode,
  baseUrl,
  isW2,
  enableFullBehavior,
  copyToClipboard
}: TabProps) => {
  return (
    <>
      <div className='-mb-4'>
        <Text size={16} weight='bold' tag='h2'>
          レコメンド表示コード
        </Text>
        <Text size={12} color={TextColor.SECONDARY}>
          各ページにレコメンド表示枠を埋め込むためのコードです。事前に配信管理で配信を作成して埋め込みコードを用意してください。
        </Text>
      </div>

      {/* 8. 全ページ共通（トップページ等） レコメンド */}
      <TagSection
        title='レコメンドコード（トップページ・全ページ共通）'
        desc='トップページなど、商品やカテゴリの文脈を持たないページに設置してください。閲覧中の対象を渡さないため、どのページにも同じコードで置けます。Personal（パーソナライズ）・Ranking Blend（人気×パーソナル）・Recently Viewed（最近見たものの続き）・人気の記事のレコメンドパーツが対象です。'
        code={`<!-- W2SmartRec 全ページ共通 レコメンドパーツ -->
<script>
  var _w2SmartRec = _w2SmartRec || [];
  _w2SmartRec.push(['recommend', {
    spec_id: '{埋め込みコード}',
    container_id: 'w2sr-recommend'
  }]);
</script>
<div id="w2sr-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_03'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-recommend']
          ]}
        />
        <div className='mt-3'>
          <Text size={12} color={TextColor.SECONDARY}>
            ※ item_id・category_id
            は不要です。訪問者のクッキー（w2_vid）から行動履歴を取得してレコメンドします。
            下の「マイページ」用と中身は同じで、設置先に合わせて container_id を分けているだけです。
            商品IDを必要とするロジック（BB・OB・Similar・この商品に関連する記事）はこのコードでは正しく動きません。
          </Text>
        </div>
      </TagSection>

      {/* 9. 商品カテゴリページ レコメンド */}
      <TagSection
        title='レコメンドコード（カテゴリページ）'
        desc='カテゴリ一覧ページに設置してください。閲覧中カテゴリの人気商品やパーソナルレコメンドを表示します。'
        code={`<!-- W2SmartRec 商品カテゴリページ レコメンドパーツ -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['recommend', {\n    spec_id: '{埋め込みコード}',\n    category_id: '{商品カテゴリID}',\n    container_id: 'w2sr-category-recommend'\n  }]);\n</script>\n<div id="w2sr-category-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_04'],
            ['category_id', 'カテゴリID（閲覧中のカテゴリ）', 'CAT01'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-category-recommend']
          ]}
        />
      </TagSection>

      {/* 10. 商品詳細ページ レコメンド */}
      <TagSection
        title='レコメンドコード（商品詳細ページ）'
        desc='商品詳細ページに設置してください。閲覧中の商品に基づくレコメンドを表示します。BB（買った人はこちらも）・OB（見た人はこちらを購入）・Similar（類似商品）、および「この商品に関連する記事」のレコメンドパーツが対象です。'
        code={`<!-- W2SmartRec 商品詳細ページ レコメンドパーツ -->
<script>
  var _w2SmartRec = _w2SmartRec || [];
  _w2SmartRec.push(['recommend', {
    spec_id: '{埋め込みコード}',
    item_id: '{商品ID}',
    container_id: 'w2sr-product-recommend'
  }]);
</script>
<div id="w2sr-product-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_01'],
            ['item_id', '表示中の商品ID', 'SKU001'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-product-recommend']
          ]}
        />
        <div className='mt-3'>
          <Text size={12} color={TextColor.SECONDARY}>
            ※
            レイアウトが「バンドル（まとめてカートに入れる）」のレコメンドパーツも、このコードで設置します。item_id
            の商品が主商品になります。見た目を変えるときのクラス名は下の「バンドルのカスタムCSSクラス名」を参照してください。
          </Text>
        </div>
      </TagSection>

      {/* 10-2. バンドルのカスタムCSSクラス名 */}
      <BundleCssClassSection />

      {/* 11. 特集ページ レコメンド（記事・商品の両方） */}
      <TagSection
        title='レコメンドコード（特集ページ）'
        desc='特集ページに設置してください。閲覧中の特集に基づくレコメンドを表示します。関連記事・人気記事（記事レコメンド）に加え、「この特集に関連する商品」「この特集を見た人はこちらも見ています」（商品レコメンド）のレコメンドパーツが対象です。'
        code={`<!-- W2SmartRec 特集ページ レコメンドパーツ -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['recommend', {\n    spec_id: '{埋め込みコード}',\n    feature_page_id: '{特集ページID}',\n    container_id: 'w2sr-article-recommend'\n  }]);\n</script>\n<div id="w2sr-article-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'ARTICLE_REC_01'],
            ['feature_page_id', '現在表示中の特集ページID', 'FP001'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-article-recommend']
          ]}
        />
        <div className='mt-3'>
          <Text size={12} color={TextColor.SECONDARY}>
            ※
            「この特集に関連する商品」は、その特集に既に掲載されている商品を除いて表示します。ページに並んでいる商品を再掲しないためです。
          </Text>
        </div>
      </TagSection>

      {/* 12. カートページ レコメンド */}
      <TagSection
        title='レコメンドコード（カートページ）'
        desc='カートページのカートコードの下に設置してください。カート内商品に関連するおすすめ商品を表示します。BB（買った人はこちらも）ロジックのレコメンドパーツが最適です。'
        code={`<!-- W2SmartRec カートページ レコメンドパーツ -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['recommend', {\n    spec_id: '{埋め込みコード}',\n    item_id: '{カート内の代表商品ID}',\n    container_id: 'w2sr-cart-recommend'\n  }]);\n</script>\n<div id="w2sr-cart-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_01'],
            ['item_id', 'カート内の代表商品ID（最初の商品等）', 'SKU001'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-cart-recommend']
          ]}
        />
      </TagSection>

      {/* 13. 注文完了ページ レコメンド */}
      <TagSection
        title='レコメンドコード（注文完了ページ）'
        desc='購入完了ページの購入完了コードの下に設置してください。購入した商品に関連する「次もおすすめ」商品を表示します。'
        code={`<!-- W2SmartRec 注文完了ページ レコメンドパーツ -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['recommend', {\n    spec_id: '{埋め込みコード}',\n    item_id: '{購入した代表商品ID}',\n    container_id: 'w2sr-purchase-recommend'\n  }]);\n</script>\n<div id="w2sr-purchase-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_02'],
            ['item_id', '購入した代表商品ID（最初の商品等）', 'SKU001'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-purchase-recommend']
          ]}
        />
      </TagSection>

      {/* 14. マイページ レコメンド */}
      <TagSection
        title='レコメンドコード（マイページ）'
        desc='マイページのトップに設置してください。ユーザーの閲覧・購入履歴に基づくパーソナルレコメンドを表示します。personalロジックのレコメンドパーツが最適です。コードの中身は「トップページ・全ページ共通」と同じで、container_id だけが異なります。'
        code={`<!-- W2SmartRec マイページ レコメンドパーツ -->\n<script>\n  var _w2SmartRec = _w2SmartRec || [];\n  _w2SmartRec.push(['recommend', {\n    spec_id: '{埋め込みコード}',\n    container_id: 'w2sr-mypage-recommend'\n  }]);\n</script>\n<div id="w2sr-mypage-recommend"></div>`}
        copyToClipboard={copyToClipboard}
      >
        <ParamTable
          rows={[
            ['spec_id', '配信の埋め込みコード（配信管理で確認）', 'RECOMMEND_03'],
            ['container_id', 'レコメンド表示先のHTML要素ID', 'w2sr-mypage-recommend']
          ]}
        />
        <div className='mt-3'>
          <Text size={12} color={TextColor.SECONDARY}>
            ※
            item_id不要。ユーザーのクッキー（w2_vid）から行動履歴を自動取得し、パーソナライズされたレコメンドを表示します。
          </Text>
        </div>
      </TagSection>
    </>
  )
}

type TabType = 'tracking' | 'display' | 'mail'

/* ============================
   EmbedTagGuide
   ============================ */
interface EmbedTagGuideProps {
  siteId: number
  distributionCode: string
  isW2: boolean
  copyToClipboard: (text: string) => void
  publicBaseUrl: string
  enableFullBehavior: boolean
  // 2026-10-06 追加: メールレコメンド（サイトの enable_mail_reco）。OFF ならメール用タブを出さない
  mailRecoEnabled: boolean
}

const EmbedTagGuide = ({
  siteId,
  distributionCode,
  isW2,
  copyToClipboard,
  publicBaseUrl,
  enableFullBehavior,
  mailRecoEnabled
}: EmbedTagGuideProps) => {
  // タブは URL クエリ（?tab=display 等）と同期させる。
  // セットアップガイドなど他画面から特定のタブへ直接リンクできるようにするため
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromQuery = searchParams.get('tab')
  // メールレコメンドが OFF のサイトでは ?tab=mail で開かれてもトラッキングコードを出す
  const tab: TabType =
    tabFromQuery === 'display' || (tabFromQuery === 'mail' && mailRecoEnabled)
      ? tabFromQuery
      : 'tracking'
  const setTab = (next: TabType) => {
    const params = new URLSearchParams(searchParams)
    params.set('tab', next)
    setSearchParams(params, { replace: true })
  }
  const baseUrl = publicBaseUrl || window.location.origin

  return (
    <>
      <Area>
        <TabList>
          <Tab selected={tab === 'tracking'} onClick={() => setTab('tracking')}>
            トラッキングコード
          </Tab>
          <Tab selected={tab === 'display'} onClick={() => setTab('display')}>
            表示コード
          </Tab>
          {mailRecoEnabled && (
            <Tab selected={tab === 'mail'} onClick={() => setTab('mail')}>
              メール用レコメンド
            </Tab>
          )}
        </TabList>
      </Area>
      {tab === 'tracking' ? (
        <TrackingTab
          distributionCode={distributionCode}
          baseUrl={baseUrl}
          isW2={isW2}
          enableFullBehavior={enableFullBehavior}
          copyToClipboard={copyToClipboard}
        />
      ) : tab === 'display' ? (
        <DisplayTab
          distributionCode={distributionCode}
          baseUrl={baseUrl}
          isW2={isW2}
          enableFullBehavior={enableFullBehavior}
          copyToClipboard={copyToClipboard}
        />
      ) : (
        <MailTab siteId={siteId} copyToClipboard={copyToClipboard} />
      )}
    </>

    //   <Area>
    //     <div className='p-6'>
    //       <Text size={14} weight='bold'>
    //         recommend コマンド パラメータ詳細
    //       </Text>
    //       <div className='mt-3 overflow-hidden rounded-lg border border-[rgba(16,185,129,0.2)] bg-[rgba(16,185,129,0.06)]'>
    //         <table className='w-full border-collapse text-[12px]'>
    //           <thead>
    //             <tr className='border-b border-[rgba(16,185,129,0.2)]'>
    //               <th className='px-3 py-2 text-left font-bold text-[#64748b]'>パラメータ</th>
    //               <th className='px-3 py-2 text-left font-bold text-[#64748b]'>必須</th>
    //               <th className='px-3 py-2 text-left font-bold text-[#64748b]'>説明</th>
    //               <th className='px-3 py-2 text-left font-bold text-[#64748b]'>例</th>
    //             </tr>
    //           </thead>
    //           <tbody>
    //             {[
    //               ['spec_id', '必須', '配信コード', 'RECOMMEND_01'],
    //               ['container_id', '任意', '描画先のHTML要素ID', 'w2sr-RECOMMEND_01'],
    //               ['limit', '任意', '表示件数（デフォルト: レコメンドパーツ設定値）', '6'],
    //               [
    //                 'item_id',
    //                 'BB/OB/similar は必須',
    //                 '現在表示中の商品ID。BB・OB・similarロジックでは必須（personalでは不要）',
    //                 'SKU001'
    //               ],
    //               ['category_id', '任意', 'カテゴリID', 'CAT01'],
    //               [
    //                 'callback',
    //                 '任意',
    //                 'レスポンスを受け取るコールバック関数',
    //                 'function(data){...}'
    //               ],
    //               ['template_id', '任意', 'カスタムテンプレートID（将来拡張用）', 'custom_01']
    //             ].map(([param, required, desc, example], i) => (
    //               <tr key={i} className='border-b border-[rgba(16,185,129,0.1)] last:border-0'>
    //                 <td className='px-3 py-2'>
    //                   <code className='rounded bg-[rgba(0,0,0,0.06)] px-1 py-0.5 text-[11px]'>
    //                     {param}
    //                   </code>
    //                 </td>
    //                 <td className='px-3 py-2'>
    //                   {required === '必須' || required.includes('は必須') ? (
    //                     <span className='rounded bg-[rgba(245,158,11,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#b45309]'>
    //                       {required}
    //                     </span>
    //                   ) : (
    //                     <Text size={12} color={TextColor.SECONDARY}>
    //                       {required}
    //                     </Text>
    //                   )}
    //                 </td>
    //                 <td className='px-3 py-2 text-[#334155]'>{desc}</td>
    //                 <td className='px-3 py-2 text-[#334155]'>{example}</td>
    //               </tr>
    //             ))}
    //           </tbody>
    //         </table>
    //       </div>
    //     </div>
    //   </Area>
    // </div>
  )
}

/* ============================
   TagManagerPage
   ============================ */
const TagManagerPage = () => {
  const siteId = useSiteId()

  const { data: site, isLoading: siteLoading } = useSiteGetApi({ site_id: siteId })
  const { data: systemConfigs } = useSystemConfigGetApi()

  const publicBaseUrl = systemConfigs?.find((c) => c.key === 'public_base_url')?.value ?? ''

  const { addMessage } = useContext(ToastMessageContext)
  const copyToClipboard = (text: string) => {
    copyTextToClipboard(text)
    addMessage({
      text: 'コードをコピーしました',
      type: MessageType.SUCCESS
    })
  }

  const distributionCode = site?.distribution_code ?? '{配信コード}'
  // 全サイトが W2Commerce 完全連動。連動していることの案内は常に出す
  const isW2 = true

  return (
    <Main type={MainLayoutType.SMALL}>
      <PageTitle
        title='埋め込みコード一覧'
        badgeNode={<AppTitleBadge type='recommend' />}
        description='ECサイトへの計測コード・レコメンドパーツの埋め込みガイド'
      />

      {siteLoading ? (
        <PageListLoading small />
      ) : (
        <Content>
          <EmbedTagGuide
            siteId={siteId}
            distributionCode={distributionCode}
            isW2={isW2}
            copyToClipboard={copyToClipboard}
            publicBaseUrl={publicBaseUrl}
            enableFullBehavior={!!site?.enable_full_behavior}
            mailRecoEnabled={!!site?.enable_mail_reco}
          />
        </Content>
      )}
    </Main>
  )
}

export default TagManagerPage
